import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { z } from "zod";

import {
  FilterPanel,
  type FilterPanelStatus,
  type FilterSeed,
  type FilterState,
} from "@/components/landing/FilterPanel";
import { PennButton } from "@/components/ui/button/PennButton";
import { SolutionGrid } from "@/components/landing/SolutionGrid";
import { ComparisonList } from "@/components/landing/ComparisonList";
import { ApplicationParameters } from "@/components/landing/ApplicationParameters";
import { FloatingCompareCTA } from "@/components/landing/FloatingCompareCTA";
import { SupportModal, type RfqContext } from "@/components/popup/SupportModal";
import { dispatchSupportAssistantProgress } from "@/components/constant/SupportAssistant";
import { trackEvent } from "@penn/web-common";
import { ApplicationEditorModal } from "@/components/popup/applications/ApplicationEditorModal";
import { ExistingApplicationsModal } from "@/components/popup/applications/ExistingApplicationsModal";
import {
  ConfigureSolutionModal,
  type ConfigurePayload,
} from "@/components/popup/configure/ConfigureSolutionModal";
import type { RegionCode, SelectorEvalResponse, SelectorProduct } from "@/api/filter";
import { evalSelector } from "@/api/filter";
import type { ApplicationSummary, ApplicationDetail } from "@/api/applications";
import { useAppDispatch, useAppSelector } from "@/stores/hooks";
import {
  clearSelector,
  setLastConfigured,
  setSelectorApps,
  syncLastConfiguredContext,
} from "@/api/redux-toolkit/reducer/selectorSlice";
import { setSelectedRegion } from "@penn/web-common";
import { useRegion } from "@/hooks/useRegion";
import { getApplication } from "@/api/applications";
import { deriveAppDrivenBrands } from "./app-driven-brands";
import { getSnapshot, getTask, getTaskApplications } from "@/api/tasks";
import type {
  RfqFilters,
  TaskApplicationLink,
  TaskDetail,
  TaskSnapshotSelectedOption,
} from "@/api/tasks";
import type { TaskContext } from "@/components/popup/configure/TaskSidePanel";
import {
  CUSTOM_TOOLING_PART_NO,
  clearAllProductSelections,
  loadProductSelection,
  saveProductSelection,
  type ConfigureProductSelection,
} from "@/utils/configure-storage";
import { useDownloadAdHocQuote } from "@/hooks/useQuoteDocuments";
import {
  toDocApplications,
  toDocAssumptions,
  toDocSelectedOptions,
} from "@/utils/rfq-snapshot";
import { useWorkFor } from "@/hooks/useWorkFor";
import {
  workForDisplay,
  workForFromTask,
  writeStoredWorkFor,
} from "@penn/web-common";
import { postWorkFor } from "@penn/web-common";
import {
  dispatchCompareCostInvalidate,
  WORK_FOR_CHANGED,
} from "@penn/web-common";
import { useRefreshProfile } from "@penn/web-common";
import {
  baseApplicationIdFromScoped,
  deriveAppsForCompare,
  reconcileTSeriesAssignments,
} from "@/utils/tseries-split";

type Modal = "none" | "support" | "existing-apps" | "new-app" | "edit-app" | "configure";

/**
 * Snapshot of the configure-modal context. Region is frozen at open time
 * (Q7); ignoring header region changes until the modal closes. `productId`
 * is required (Q6) — name-only opens are explicitly rejected (the
 * `FloatingCompareCTA` handler routes to the first selected productId or
 * no-ops).
 */
interface ConfigureContext {
  productId: string;
  product: SelectorProduct;
  region: RegionCode;
  applicationIds: string[];
  mspAutoFeedEnabled: boolean;
  requiredMachines: number;
  /** D2: present when Configure was opened by re-hydrating a task. Renders the
   * RFQ side panel + makes Configure persist manual line items to the task. */
  taskContext?: TaskContext;
  /** D2: frozen rows carry their configured quantities. */
  initialSelectedOptions?: TaskSnapshotSelectedOption[];
  /** Legacy part-number-only fallback. */
  initialSelectedPartNos?: string[];
  initialCustomToolingText?: string | null;
}

/**
 * D2 — build the Configure RFQ side-panel context from a re-hydrated task. The
 * workflow steps mirror the svc-07 fixed config (engineer flow = Engineering
 * Review → Sales Sign-off; sales flow = Sales Sign-off only).
 */
function buildTaskSidePanel(
  taskId: string,
  task: TaskDetail | null,
  links: TaskApplicationLink[],
  details: ApplicationDetail[],
): TaskContext {
  // `task` (the staff-only GET /v1/tasks/:id detail) is null when the caller
  // can't read it — e.g. an external_customer opening THEIR OWN task: the
  // detail endpoint is gated `canWorkForOthers` (403), while the snapshot +
  // application links ARE owner-readable. The panel is driven by `taskId` +
  // those owner-readable rows; the staff-only metadata below degrades to
  // defaults rather than blocking the customer's own-task view.
  // POC parity: the customer label leads with the person's NAME (work-for target
  // first, else the requester), then email; the company name is only a last
  // resort — it must never stand in for the user name.
  const customer =
    [task?.workingForFirstName, task?.workingForLastName].filter(Boolean).join(" ").trim() ||
    [task?.requestedByFirstName, task?.requestedByLastName].filter(Boolean).join(" ").trim() ||
    task?.workingForEmail ||
    task?.requestedByEmail ||
    task?.requestedByCompanyName ||
    "—";
  // POC-parity broken-out customer block. When the task is a staff "Work-For"
  // request, the customer IS the Work-For target — prefer those fields; fall
  // back to the requester (self-submitted) fields otherwise.
  const customerDetail = {
    companyName:
      task?.workingForCompanyName ?? task?.requestedByCompanyName ?? null,
    contactName:
      [task?.workingForFirstName, task?.workingForLastName]
        .filter(Boolean)
        .join(" ") ||
      [task?.requestedByFirstName, task?.requestedByLastName]
        .filter(Boolean)
        .join(" ") ||
      null,
    email: task?.workingForEmail ?? task?.requestedByEmail ?? null,
    phone: task?.requestedByPhone ?? null,
    address: task?.requestedByCompanyAddress ?? null,
  };
  const flowType = task?.flowType ?? "engineer";
  const workflow =
    flowType === "sales"
      ? ["Sales Sign-off"]
      : ["Engineering Review", "Sales Sign-off"];
  // Enrich each frozen link with the live application row (metadata + fasteners)
  // so the RFQ-context card matches the POC. `details` are keyed by the live
  // application UUID, which equals the link's savedId (or appId for DB-backed
  // apps frozen before savedId was persisted).
  const detailById = new Map(details.map((d) => [d.applicationId, d]));
  return {
    taskId,
    meta: `${flowType} flow`,
    customer,
    customerDetail,
    note: task?.comment ?? undefined,
    workflow,
  applications: links.map((l) => l.name || l.applicationNumber || l.appId),
    // Issue 3 (POC parity): carry the saved app UUID so the task side panel can
    // render the read-only CAD preview + Files/Download-all per application.
    // Fall back to appId for pre-savedId snapshots (appId === library UUID for
    // MVP DB-backed apps).
    applicationRefs: links.map((l) => {
      const savedId = l.savedId ?? l.appId;
      const d = savedId ? detailById.get(savedId) : undefined;
      return {
        savedId,
        name: l.name || l.applicationNumber || l.appId,
        applicationNumber: d?.applicationNumber ?? l.applicationNumber ?? null,
        oem: d?.oem ?? l.oem ?? null,
        productionProgram: d?.productionProgram ?? l.productionProgram ?? null,
        materialCode: d?.materialCode ?? null,
        annualVolume: d?.annualVolume ?? null,
        batchSize: d?.batchSize ?? null,
        fasteners: (d?.fasteners ?? []).map((f) => ({
          brand: f.brand,
          type: f.type,
          qtyPerPart: f.qtyPerPart,
          qtyPerPartA: f.qtyPerPartA,
          qtyPerPartB: f.qtyPerPartB,
        })),
      };
    }),
  };
}

// D2 capture — fold the live filter chips + comparison set into the RFQ-snapshot
// filter shape (POC `filtersSnap`). `null` filters means the user never touched
// Option A; we still freeze the compare set so it restores on Open Task.
function filterStateToRfq(
  f: FilterState | null,
  compareIds: string[],
): RfqFilters {
  if (!f) return { view: "compare", compareIds };
  return {
    view: "compare",
    types: f.fasteners,
    size: f.threadSize && f.threadSize !== "Any" ? f.threadSize : null,
    pm: f.processMonitoring,
    ri: f.robotIntegration,
    forceMinKn: f.forceMin,
    forceMaxKn: f.forceMax,
    compareIds,
  };
}

function toSummary(saved: ApplicationDetail): ApplicationSummary {
  return {
    applicationId: saved.applicationId,
    name: saved.name,
    applicationNumber: saved.applicationNumber,
    oem: saved.oem,
    productionProgram: saved.productionProgram,
    materialCode: saved.materialCode,
    annualVolume: saved.annualVolume,
    fastenerCount: saved.fastenerCount,
    createdByEmail: saved.createdByEmail,
    has3d: saved.has3d,
    createdAt: saved.createdAt,
    updatedAt: saved.updatedAt,
  };
}

function newOptimizeSessionId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for non-secure contexts (dev only).
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// A guest's workspace, carried across the sign-in round trip. localStorage for
// the same reason as the OIDC flow state (the issuer redirect may land in
// another tab); single-use, and expires after the 15-minute sign-in window.
// Validated on read: the stored draft may come from an older build.
const GUEST_WORKSPACE_KEY = "selectorGuestWorkspace";
const GUEST_WORKSPACE_TTL_MS = 15 * 60 * 1000;

const guestWorkspaceSchema = z.object({
  savedAt: z.number(),
  region: z.enum(["EU", "NA", "ASIA"]),
  filters: z
    .object({
      fasteners: z.array(z.string()),
      threadSize: z.string(),
      processMonitoring: z.boolean(),
      robotIntegration: z.boolean(),
      forceMin: z.number(),
      forceMax: z.number(),
    })
    .nullable(),
  selected: z.array(z.string()),
  mspAutoFeedByProductId: z.record(z.string(), z.boolean()),
  // Entry shape is normalized by configure-storage on every read.
  configureSelections: z.record(
    z.string(),
    z.custom<ConfigureProductSelection>(
      (value) => typeof value === "object" && value !== null,
    ),
  ),
});

type GuestWorkspace = z.infer<typeof guestWorkspaceSchema>;

function stashGuestWorkspace(workspace: GuestWorkspace): void {
  try {
    window.localStorage.setItem(GUEST_WORKSPACE_KEY, JSON.stringify(workspace));
  } catch {
    // Blocked storage / quota — the guest starts afresh after sign-in.
  }
}

function takeGuestWorkspace(): GuestWorkspace | null {
  try {
    const raw = window.localStorage.getItem(GUEST_WORKSPACE_KEY);
    window.localStorage.removeItem(GUEST_WORKSPACE_KEY);
    if (!raw) return null;
    const parsed = guestWorkspaceSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) return null;
    return Date.now() - parsed.data.savedAt < GUEST_WORKSPACE_TTL_MS
      ? parsed.data
      : null;
  } catch {
    return null;
  }
}

export const FilterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const user = useAppSelector((state) => state.auth.user);
  const assumptions = useAppSelector((state) => state.auth.assumptions);
  const region = useRegion();
  const actingForOther = !!workForDisplay(useWorkFor());
  const refreshProfile = useRefreshProfile();
  const downloadAdHocQuote = useDownloadAdHocQuote();
  // Strict price permission (Lars matrix). Gates the Configure popup's itemized
  // BOM prices + quote/ROI doc actions.
  const canShowPrices =
    isAuthenticated &&
    (!!user?.role?.canGenerateRoi || !!user?.role?.canSeePrices);
  // money_too (operator decision 2026-06-22 — full POC parity, overrides MD-02
  // for the COMPARISON CARDS only): indicative cost + utilization KPIs on the
  // comparison cards are shown to EVERY authenticated user, including external
  // customers. The Configure popup still hides itemized prices via the strict
  // `canShowPrices` above. See DECISIONS.md (price-visibility change-request).
  const canShowCardPrices = isAuthenticated;

  const [showSupport, setShowSupport] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const current = window.scrollY;
      if (current > lastScrollY.current && current > 50) setShowSupport(true);
      else if (current < lastScrollY.current) setShowSupport(false);
      lastScrollY.current = current;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const [selected, setSelected] = useState<string[]>([]);
  const [modal, setModal] = useState<Modal>("none");
  const [configureContext, setConfigureContext] = useState<ConfigureContext | null>(null);
  // Bumped when the Configure popup closes after a real change, so ComparisonList
  // re-runs Optimize Allocation (only once optimization is already in play).
  const [reoptimizeToken, setReoptimizeToken] = useState(0);
  // Selection context attached to an RFQ submit (null = generic support request).
  const [rfqContext, setRfqContext] = useState<RfqContext | null>(null);
  // Live `requiredMachines` per product from the comparison cost preview, kept
  // in sync by ComparisonList so the open Configure modal tracks utilization
  // changes instead of freezing the value captured at modal-open time.
  const [liveReqMachinesByProduct, setLiveReqMachinesByProduct] = useState<
    Record<string, number>
  >({});
  const [products, setProducts] = useState<SelectorProduct[]>([]);
  // D2: one-shot filter-chip restoration handed to FilterPanel when a task opens.
  const [filterSeed, setFilterSeed] = useState<FilterSeed | null>(null);
  // D2 capture: the live filter chips, mirrored from FilterPanel so the RFQ
  // snapshot can freeze them (null until the user first changes a filter).
  const [currentFilters, setCurrentFilters] = useState<FilterState | null>(null);
  const [selectedApps, setSelectedApps] = useState<ApplicationSummary[]>([]);
  const [selectedAppDetails, setSelectedAppDetails] = useState<ApplicationDetail[]>([]);
  const [appDrivenBrands, setAppDrivenBrands] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [evalStatus, setEvalStatus] = useState<FilterPanelStatus>({
    isPending: false,
    isError: false,
    errorMessage: null,
  });

  // Lifted from ComparisonList (Phase 2 — Q1, Q6, D9). The configure modal
  // needs both: `selectedMachineByApp` to derive `applicationIds` for the
  // chosen product, `mspAutoFeed` to drive D8a tooling auto-pick.
  const [selectedMachineByApp, setSelectedMachineByApp] = useState<
    Record<string, string>
  >({});
  const [mspAutoFeedByProductId, setMspAutoFeedByProductId] = useState<
    Record<string, boolean>
  >({});

  // Q-OPT-02 — one UUID per comparison workspace. Warms optimize_allocation_cache
  // on every calculate and scopes the optimize run. Reset when the workspace is
  // emptied (new comparison → new session).
  const [optimizeSessionId, setOptimizeSessionId] = useState<string>(() =>
    newOptimizeSessionId(),
  );
  // Bumping this remounts FilterPanel, resetting its internal filter chips back
  // to defaults (used by the Work-For reset below).
  const [filterResetKey, setFilterResetKey] = useState(0);
  useEffect(() => {
    if (selected.length === 0 && selectedApps.length === 0) {
      setOptimizeSessionId(newOptimizeSessionId());
    }
  }, [selected.length, selectedApps.length]);

  useEffect(() => {
    if (selected.length > 0 || selectedApps.length > 0) {
      dispatchSupportAssistantProgress("selection_changed");
    }
  }, [selected.length, selectedApps.length]);

  // Reset the whole Selector workspace to a clean slate. Same wipe as
  // "Clear selection" / "Reset filters", plus a FilterPanel remount so the
  // filter chips return to defaults and a fresh optimize session.
  const resetWorkspace = useCallback(() => {
    setSelected([]);
    setSelectedApps([]);
    setSelectedAppDetails([]);
    setAppDrivenBrands([]);
    setSelectedMachineByApp({});
    setMspAutoFeedByProductId({});
    setCurrentFilters(null);
    setFilterSeed(null);
    setEditingId(null);
    setLiveReqMachinesByProduct({});
    setOptimizeSessionId(newOptimizeSessionId());
    dispatch(clearSelector());
    setFilterResetKey((k) => k + 1);
  }, [dispatch]);

  // When the user MANUALLY switches the "Working for" target, start the new
  // customer from a clean filter/selection slate. Only a manual switch (the
  // admin console handoff, or "Work as myself") fires WORK_FOR_CHANGED; the
  // task-open flow deliberately does not (it must
  // keep the task's restored filters), so opening a task never triggers this.
  useEffect(() => {
    const onWorkForChanged = () => resetWorkspace();
    window.addEventListener(WORK_FOR_CHANGED, onWorkForChanged);
    return () => window.removeEventListener(WORK_FOR_CHANGED, onWorkForChanged);
  }, [resetWorkspace]);

  // Every sign-in path leaves this page first, so a guest's workspace is
  // stashed on unmount — including the Configure selections, which the page
  // load after an OIDC redirect clears — and restored once they are signed in.
  const guestWorkspaceRef = useRef({
    isAuthenticated,
    region,
    currentFilters,
    selected,
    mspAutoFeedByProductId,
  });
  useEffect(() => {
    guestWorkspaceRef.current = {
      isAuthenticated,
      region,
      currentFilters,
      selected,
      mspAutoFeedByProductId,
    };
  });
  useEffect(
    () => () => {
      const w = guestWorkspaceRef.current;
      if (w.isAuthenticated) return;
      if (w.selected.length === 0 && w.currentFilters === null) return;
      const configureSelections: Record<string, ConfigureProductSelection> = {};
      for (const productId of w.selected) {
        const selection = loadProductSelection(productId);
        if (selection) configureSelections[productId] = selection;
      }
      stashGuestWorkspace({
        savedAt: Date.now(),
        region: w.region,
        filters: w.currentFilters,
        selected: w.selected,
        mspAutoFeedByProductId: w.mspAutoFeedByProductId,
        configureSelections,
      });
    },
    [],
  );
  useEffect(() => {
    if (!isAuthenticated) return;
    const workspace = takeGuestWorkspace();
    if (!workspace) return;
    // Configure already drops saved part numbers the region does not offer.
    for (const [productId, selection] of Object.entries(
      workspace.configureSelections,
    )) {
      saveProductSelection(productId, selection);
    }
    setMspAutoFeedByProductId(workspace.mspAutoFeedByProductId);
    if (workspace.filters) {
      setCurrentFilters(workspace.filters);
      setFilterSeed({
        token: `guest-workspace:${workspace.savedAt}`,
        ...workspace.filters,
      });
    }
    if (workspace.region === region) {
      setSelected(workspace.selected);
      return;
    }
    // The account's region differs from the one the guest browsed: machines
    // are per-region catalog rows, so keep only those this region offers.
    const restoreOfferedMachines = async () => {
      try {
        const { products } = await evalSelector({ region });
        const offered = new Set(products.map((p) => p.productId));
        setSelected(workspace.selected.filter((id) => offered.has(id)));
      } catch (err) {
        console.error("[selector] failed to restore the guest's machines", err);
      }
    };
    void restoreOfferedMachines();
  }, [isAuthenticated, region]);

  // Mirror the local apps lists into Redux so the navbar/floating Request
  // Support button (which is rendered outside this page) can render correct
  // SOLUTION/MACHINE TYPE/INCLUDED counts in the popup.
  useEffect(() => {
    dispatch(
      setSelectorApps({
        summaries: selectedApps,
        details: selectedAppDetails,
      }),
    );
  }, [dispatch, selectedApps, selectedAppDetails]);

  // Cleanup: when an app is removed, drop its assignment.
  useEffect(() => {
    const ids = new Set(selectedApps.map((a) => a.applicationId));
    setSelectedMachineByApp((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        if (!ids.has(baseApplicationIdFromScoped(key))) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [selectedApps]);

  // ADR-0025: preserve assignment intent as T-Series selection toggles the
  // comparison between base applications and independent A/B allocation units.
  useEffect(() => {
    if (selectedAppDetails.length !== selectedApps.length) return;
    const selectedProducts = products.filter((product) =>
      selected.includes(product.productId),
    );
    const derived = deriveAppsForCompare(selectedAppDetails, selectedProducts);
    setSelectedMachineByApp((prev) =>
      reconcileTSeriesAssignments({
        assignments: prev,
        baseApplicationIds: selectedApps.map((app) => app.applicationId),
        scopedApplicationIds: derived.apps.map((app) => app.applicationId),
        splitEnabled: derived.splitEnabled,
      }),
    );
  }, [products, selected, selectedApps, selectedAppDetails]);

  const handleResults = useCallback((resp: SelectorEvalResponse | undefined) => {
    setProducts(resp?.products ?? []);
  }, []);
  const handleStatus = useCallback((s: FilterPanelStatus) => setEvalStatus(s), []);

  const open = (m: Modal) => setModal(m);
  const close = () => setModal("none");

  const requireAuth = (m: Modal) => {
    if (!isAuthenticated) {
      navigate("/login", { state: { from: { pathname: location.pathname } } });
      return;
    }
    open(m);
  };

  const toggleSolution = (id: string) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  /**
   * Open Configure Solution for a specific product. Snapshots region +
   * applicationIds at open time (Q7). Auto-feed (D9) is read live from
   * comparison state while the modal stays open. `productId` required (Q6).
   */
  // Q1: prefer apps checked for this machine; if none, scope tooling to all
  // selected apps so PEM fastener groups still load (POC getAppsForConfig).
  const configureApplicationIdsFor = useCallback(
    (productId: string) => {
      const assigned = Object.entries(selectedMachineByApp)
        .filter(([, machineId]) => machineId === productId)
        .map(([appId]) => appId);
      if (assigned.length > 0) return assigned;
      return selectedApps.map((a) => a.applicationId);
    },
    [selectedMachineByApp, selectedApps],
  );

  const appDetailsForIds = useCallback(
    (applicationIds: string[]) => {
      const idSet = new Set(applicationIds.map(baseApplicationIdFromScoped));
      const scoped = selectedAppDetails.filter((d) => idSet.has(d.applicationId));
      return scoped.length > 0 ? scoped : selectedAppDetails;
    },
    [selectedAppDetails],
  );

  const handleConfigure = useCallback(
    (productId: string, extras: { requiredMachines: number }) => {
      const product = products.find((p) => p.productId === productId);
      if (!product) return;
      const applicationIds = configureApplicationIdsFor(productId);
      setConfigureContext({
        productId,
        product,
        region,
        applicationIds,
        mspAutoFeedEnabled: mspAutoFeedByProductId[productId] ?? true,
        requiredMachines: extras.requiredMachines,
      });
      // POC parity: write the latest Configure click into Redux so the navbar
      // / floating Request Support popup auto-fills SOLUTION/MACHINE TYPE.
      dispatch(
        setLastConfigured({
          solutionProductId: productId,
          solutionName: product.name,
          solutionMachineType:
            product.subcategory || product.category || null,
          applications: appDetailsForIds(applicationIds),
          applicationIds,
          selectedOptions: [],
          selectedPartNos: [],
          manualLineItems: [],
          region,
          mspAutoFeedEnabled: mspAutoFeedByProductId[productId] ?? true,
          requiredMachines: extras.requiredMachines,
          filters: filterStateToRfq(currentFilters, selected),
        }),
      );
      open("configure");
    },
    [
      dispatch,
      products,
      region,
      configureApplicationIdsFor,
      appDetailsForIds,
      mspAutoFeedByProductId,
      currentFilters,
      selected,
    ],
  );

  // The RFQ popup can be opened straight from the Selector (navbar / floating
  // "Request Support"), i.e. without ever opening Configure. Mirror the machine
  // the user has selected into Redux so that popup describes the same solution —
  // it derives the recommended option set from this entry
  // (useConfigureSelectionPreview). Once this machine is the configured one,
  // keep its live application/configuration inputs synchronized without
  // discarding manual line items or an unchanged deliberate option selection.
  const lastConfiguredProductId = useAppSelector(
    (s) => s.selector.lastConfigured?.solutionProductId ?? null,
  );
  useEffect(() => {
    const assigned = Array.from(
      new Set(
        Object.values(selectedMachineByApp).filter((id) =>
          selected.includes(id),
        ),
      ),
    );
    const unambiguousProductId =
      assigned.length === 1
        ? assigned[0]!
        : selected.length === 1
          ? selected[0]!
          : null;
    // With several compared machines, keep the most recently configured one
    // live as long as it is still selected.
    const productId =
      unambiguousProductId ??
      (lastConfiguredProductId &&
        selected.includes(lastConfiguredProductId)
        ? lastConfiguredProductId
        : null);
    if (!productId) {
      // Drop a removed solution so the popup never offers a machine that is no
      // longer part of the comparison workspace.
      if (
        lastConfiguredProductId &&
        !selected.includes(lastConfiguredProductId)
      ) {
        dispatch(setLastConfigured(null));
      }
      return;
    }
    const applicationIds = configureApplicationIdsFor(productId);
    if (productId === lastConfiguredProductId) {
      dispatch(
        syncLastConfiguredContext({
          solutionProductId: productId,
          applications: appDetailsForIds(applicationIds),
          applicationIds,
          region,
          mspAutoFeedEnabled: mspAutoFeedByProductId[productId] ?? true,
          requiredMachines: liveReqMachinesByProduct[productId] ?? 1,
          filters: filterStateToRfq(currentFilters, selected),
        }),
      );
      return;
    }
    const product = products.find((p) => p.productId === productId);
    if (!product) return;
    dispatch(
      setLastConfigured({
        solutionProductId: productId,
        solutionName: product.name,
        solutionMachineType: product.subcategory || product.category || null,
        applications: appDetailsForIds(applicationIds),
        applicationIds,
        selectedOptions: [],
        selectedPartNos: [],
        manualLineItems: [],
        region,
        mspAutoFeedEnabled: mspAutoFeedByProductId[productId] ?? true,
        requiredMachines: liveReqMachinesByProduct[productId] ?? 1,
        filters: filterStateToRfq(currentFilters, selected),
      }),
    );
  }, [
    dispatch,
    selected,
    selectedMachineByApp,
    lastConfiguredProductId,
    products,
    region,
    configureApplicationIdsFor,
    appDetailsForIds,
    mspAutoFeedByProductId,
    liveReqMachinesByProduct,
    currentFilters,
  ]);

  const handleAppsSelected = useCallback(async (apps: ApplicationSummary[]) => {
    // Any change to the application scope changes which options the API
    // recommends, so saved Configure selections no longer apply. Opening a task
    // is unaffected: it seeds Configure from the frozen snapshot, not storage.
    clearAllProductSelections();
    // Enforce one entry per applicationId. An application can only be applied
    // once: editing-and-saving an already-selected app must replace, never
    // append a second copy.
    const deduped = Array.from(
      new Map(apps.map((a) => [a.applicationId, a])).values(),
    );
    setSelectedApps(deduped);
    if (deduped.length === 0) {
      setAppDrivenBrands([]);
      setSelectedAppDetails([]);
      return;
    }
    try {
      const details = await Promise.all(
        deduped.map((a) => getApplication(a.applicationId)),
      );
      setSelectedAppDetails(details);
      // Mirror POC applyAppDrivenFilters — map each
      // application's fastener lines to the manual filter-bar chips (issue 7).
      setAppDrivenBrands(
        deriveAppDrivenBrands(details.flatMap((detail) => detail.fasteners)),
      );
    } catch {
      setAppDrivenBrands([]);
      setSelectedAppDetails([]);
    }
  }, []);

  // C3: applications chosen in the NavBar "My applications" modal are handed to
  // this page through navigation state (the NavBar renders outside the page).
  // Apply them on arrival, then clear the state so a refresh / back-navigation
  // doesn't re-apply the same selection.
  useEffect(() => {
    const navState = location.state as
      | { applyApps?: ApplicationSummary[] }
      | null;
    const apps = navState?.applyApps;
    if (!apps || apps.length === 0) return;
    void handleAppsSelected(apps);
    navigate(location.pathname, { replace: true, state: null });
  }, [location, handleAppsSelected, navigate]);

  // Restore the applications behind a task's frozen snapshot links (MVP apps are
  // DB-backed; ad-hoc links carry no savedId and are skipped). `appId` is the
  // fallback for snapshots frozen before savedId was persisted — a genuinely
  // ad-hoc id just 404s and is dropped by allSettled. handleAppsSelected also
  // re-derives the app-driven fastener filters (POC applyAppDrivenFilters).
  const restoreTaskApplications = useCallback(
    async (taskId: string) => {
      const links = await getTaskApplications(taskId);
      const savedIds = links
        .map((l) => l.savedId ?? l.appId)
        .filter((x): x is string => !!x);
      let details: ApplicationDetail[] = [];
      if (savedIds.length > 0) {
        const settled = await Promise.allSettled(
          savedIds.map((id) => getApplication(id)),
        );
        details = settled.flatMap((r) =>
          r.status === "fulfilled" ? [r.value] : [],
        );
        await handleAppsSelected(details.map(toSummary));
      }
      return { links, details };
    },
    [handleAppsSelected],
  );

  // D2 — open a task from the Tasks queue. The NavBar Tasks modal hands the task
  // id here via router state; re-hydrate the selector from the frozen snapshot
  // (region + applications + comparison selection), then open Configure with the
  // restored options + the RFQ side panel — POC `openTaskFromList` /
  // `applyTaskSnapshot`. The snapshot read is
  // owner/Work-For/admin-gated (Lars matrix): a non-owner queue task returns no
  // snapshot, so we surface a friendly message instead of crashing.
  const processedNavKeyRef = useRef<string | null>(null);
  const deepLinkTaskRef = useRef<string | null>(null);
  const rehydrateFromTask = useCallback(
    async (taskId: string) => {
      // Opening a saved task is "use an existing application" — the page
      // itself is public, but this action still requires a signed-in session
      // (task/snapshot reads are permission-gated server-side anyway).
      if (!isAuthenticated) {
        navigate("/login", { state: { from: { pathname: location.pathname } } });
        return;
      }
      try {
        // The task-detail read (GET /v1/tasks/:id) is staff-only
        // (`canWorkForOthers`); an external_customer opening THEIR OWN task 403s
        // there but CAN read the owner-gated snapshot + application links. The
        // customer view is rehydrated entirely from the snapshot, so a
        // task-detail failure must NOT abort the open — fall back to a null task.
        const [task, snapshot] = await Promise.all([
          getTask(taskId).catch(() => null),
          getSnapshot(taskId),
        ]);
        if (!snapshot || !snapshot.solutionProductId) {
          // Draft/general-support tasks and older failed RFQ submissions can
          // legitimately have no machine snapshot, so there is no machine to
          // configure. Wipe the previous workspace (filter chips back to
          // defaults) and fill back only what this task actually carries — its
          // region and its applications, which re-derive the app-driven filters
          // — then return the user to the application inputs.
          resetWorkspace();
          dispatch(
            setSelectedRegion((snapshot?.filterRegion as RegionCode) || region),
          );
          await restoreTaskApplications(taskId);
          document
            .getElementById("application-parameters")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }

        // POC applyTaskSnapshot parity (R-5): loading a task switches Working-For
        // to the task's customer so edits + a re-quote stay on their behalf
        // (otherwise the new task's customer silently becomes the opener). The
        // switch is visible via the nav "Working for" banner and persists until
        // the user changes it — no auto-revert (MD-12: sales iterate on quotes).
        // Applied server-side FIRST so an unauthorized target (e.g. cross-region
        // for home-only roles) is rejected and the current context is kept.
        if (task && user?.id && user.role?.canWorkForOthers) {
          const target = workForFromTask(task, user.id, user.email ?? "");
          if (target) {
            try {
              await postWorkFor(
                target.kind === "registered"
                  ? { userId: target.userId }
                  : { email: target.email },
              );
              writeStoredWorkFor(target);
              dispatchCompareCostInvalidate();
              void refreshProfile();
            } catch {
              // Target not permitted for this caller — open the task as-is.
            }
          }
        }

        const solutionProductId = snapshot.solutionProductId;
        const regionCode = (snapshot.filterRegion as RegionCode) || region;
        dispatch(setSelectedRegion(regionCode));

        // PENN-091: give this task review its own optimize workspace. A fresh
        // session id means the reviewed task's Optimize Allocation cannot reuse
        // a previous workspace's cached allocation (or vice-versa), so the app
        // IDs carried by a closed review never silently leak into an unrelated
        // optimize run. The session is likewise discarded when the workspace is
        // later emptied (see the selected/selectedApps effect above).
        setOptimizeSessionId(newOptimizeSessionId());

        // Restore the manual filter chips (types/size/PM/RI/force) so the panel
        // matches the frozen request — POC applyTaskSnapshot. Token is unique per
        // open so re-opening the same task re-applies even after manual edits.
        setFilterSeed({
          token: `${taskId}:${Date.now()}`,
          fasteners: snapshot.filterTypes ?? [],
          threadSize:
            snapshot.filterSize && snapshot.filterSize.trim()
              ? snapshot.filterSize
              : "Any",
          processMonitoring: !!snapshot.filterPm,
          robotIntegration: !!snapshot.filterRi,
          ...(snapshot.filterForceMinKn != null
            ? { forceMin: snapshot.filterForceMinKn }
            : {}),
          ...(snapshot.filterForceMaxKn != null
            ? { forceMax: snapshot.filterForceMaxKn }
            : {}),
        });

        const { links, details } = await restoreTaskApplications(taskId);

        // Resolve the SelectorProduct via a region-only eval (the broad product
        // set contains the solution if it's still in the catalog).
        const evalResp = await evalSelector({ region: regionCode });
        setProducts(evalResp.products);
        const product = evalResp.products.find(
          (p) => p.productId === solutionProductId,
        );

        if (!product) {
          // The task's machine is no longer in the catalog, so there is again
          // nothing to configure. Leave the restored applications in place but
          // drop the machine selection rather than pointing the workspace at a
          // machine that doesn't exist.
          setSelected([]);
          setSelectedMachineByApp({});
          dispatch(clearSelector());
          window.alert(
            "Loaded the task's region and applications, but its solution machine isn't in the current catalog.",
          );
          document
            .getElementById("application-parameters")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }

        // Restore the comparison selection + assign the restored apps to the
        // solution machine (so the comparison card + Configure scope match).
        setSelected(
          Array.from(
            new Set([solutionProductId, ...(snapshot.filterCompareIds ?? [])]),
          ),
        );
        const machineMap: Record<string, string> = {};
        for (const d of details) machineMap[d.applicationId] = solutionProductId;
        setSelectedMachineByApp(machineMap);

        setConfigureContext({
          productId: solutionProductId,
          product,
          region: regionCode,
          applicationIds: details.map((d) => d.applicationId),
          mspAutoFeedEnabled: true,
          requiredMachines: 1,
          taskContext: buildTaskSidePanel(taskId, task, links, details),
          initialSelectedOptions: snapshot.selectedOptions,
          initialSelectedPartNos: snapshot.selectedPartNos,
          initialCustomToolingText:
            snapshot.selectedOptions.find(
              (o) => o.partNo === CUSTOM_TOOLING_PART_NO,
            )?.requirements ?? null,
        });
        // open("configure"); — setModal is the stable primitive behind `open`,
        // so we call it directly to keep this useCallback dependency-free.
        setModal("configure");
      } catch (err) {
        console.error("[D2] failed to open task in selector", err);
        window.alert("Could not open this task. Please try again.");
      }
    },
    [dispatch, region, restoreTaskApplications, resetWorkspace, user, refreshProfile, isAuthenticated, navigate, location],
  );

  useEffect(() => {
    const navState = location.state as { openTaskId?: string } | null;
    const openTaskId = navState?.openTaskId;
    if (!openTaskId) return;
    // Dedupe on the navigation's unique key (not the taskId) so re-opening the
    // SAME task works, while React's double-invoke / unrelated re-renders don't
    // fire it twice.
    if (processedNavKeyRef.current === location.key) return;
    processedNavKeyRef.current = location.key;
    // Clear router state so a refresh / back-navigation doesn't re-open it.
    navigate(location.pathname, { replace: true, state: null });
    // Single "View" (POC openTaskFromList) → rehydrate the selector from the
    // frozen snapshot and open Configure with the RFQ side panel.
    void rehydrateFromTask(openTaskId);
  }, [location, navigate, rehydrateFromTask]);

  // Deep link from the RFQ email's "View in Installation System" CTA:
  // /installation-system?openTaskId=<id>&action=view
  // Opens the task into the selector via the SAME path as the in-app "View"
  // action. The page itself is public; `rehydrateFromTask` sends an
  // unauthenticated caller to /login instead of fetching the task. The query
  // is stripped afterwards so a refresh / back-navigation doesn't re-open it.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openTaskId = params.get("openTaskId");
    if (!openTaskId) return;
    if (deepLinkTaskRef.current === openTaskId) return;
    deepLinkTaskRef.current = openTaskId;
    params.delete("openTaskId");
    params.delete("action");
    const rest = params.toString();
    navigate(rest ? `${location.pathname}?${rest}` : location.pathname, {
      replace: true,
    });
    void rehydrateFromTask(openTaskId);
  }, [location, navigate, rehydrateFromTask]);

  const handleEditApp = (app: ApplicationSummary) => {
    setEditingId(app.applicationId);
    open("edit-app");
  };

  const handleSetupNewApplication = () => {
    if (!isAuthenticated) {
      navigate("/login", { state: { from: { pathname: location.pathname } } });
      return;
    }
    // Always open the blank "New Application" editor (create mode), regardless
    // of how many applications are already selected.
    setEditingId(null);
    open("new-app");
  };

  const handleSaved = (saved: ApplicationDetail) => {
    const summary = toSummary(saved);
    let next: ApplicationSummary[] = [];
    setSelectedApps((prev) => {
      // Replace the existing row by applicationId; append only when truly new.
      // Building via Map guarantees uniqueness even if the previous list ever
      // contained a stray duplicate (defence-in-depth — the editor must never
      // produce a second copy of the same application).
      const byId = new Map(prev.map((p) => [p.applicationId, p]));
      byId.set(saved.applicationId, summary);
      next = Array.from(byId.values());
      return next;
    });
    void handleAppsSelected(next);
    setEditingId(null);
  };

  const appDrivenIds = useMemo(
    () => selectedApps.map((a) => a.applicationId),
    [selectedApps],
  );

  const handleFloatingConfigure = () => {
    const el = document.getElementById("comparison-list");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const appsForConfigure = useCallback(
    (ctx: ConfigureContext): ApplicationDetail[] => {
      const baseIds = new Set(
        ctx.applicationIds.map(baseApplicationIdFromScoped),
      );
      const scoped = selectedAppDetails.filter((d) =>
        baseIds.has(d.applicationId),
      );
      return scoped.length > 0 ? scoped : selectedAppDetails;
    },
    [selectedAppDetails],
  );

  // D7 (POC parity): the Configure header shows
  // "Applications: N • Element types: M" for the apps assigned to this machine.
  // Element types = unique "BRAND TYPE" across those apps' fastener lines that
  // carry a positive quantity.
  const configureAppSummary = useMemo(() => {
    if (!configureContext) return undefined;
    const apps = appsForConfigure(configureContext);
    const applications = apps.map(
      (a) => a.name || a.applicationNumber || a.applicationId,
    );
    const elementSet = new Set<string>();
    for (const a of apps) {
      for (const f of a.fasteners) {
        const qty =
          (f.qtyPerPart ?? 0) + (f.qtyPerPartA ?? 0) + (f.qtyPerPartB ?? 0);
        if (qty <= 0) continue;
        const key = `${f.brand ?? ""} ${f.type ?? ""}`.trim().toUpperCase();
        if (key) elementSet.add(key);
      }
    }
    return { applications, elementTypes: Array.from(elementSet) };
  }, [configureContext, appsForConfigure]);

  // Configure → "Request Quotation / Support": freeze the current selection as
  // an RFQ snapshot context, then open the request modal (one-shot RFQ). Manual
  // line items ride along so SupportModal can attach them to the created task.
  // The Configure modal itself is reachable without signing in, so this action
  // needs the same gate as the floating "Request Support" button
  // (`requireAuth`) — redirect to /login instead of opening the popup.
  const handleRequestQuotation = (payload: ConfigurePayload) => {
    if (!isAuthenticated) {
      navigate("/login", { state: { from: { pathname: location.pathname } } });
      return;
    }
    if (configureContext) {
      const apps = appsForConfigure(configureContext);
      const machineType =
        configureContext.product.subcategory ||
        configureContext.product.category ||
        null;
      const rfqFilters = filterStateToRfq(currentFilters, selected);
      setRfqContext({
        solutionProductId: configureContext.productId,
        solutionName: configureContext.product.name,
        solutionMachineType: machineType,
        regionCode: configureContext.region,
        applications: apps,
        selectedOptions: payload.selectedOptions,
        selectedPartNos: payload.selectedPartNos,
        manualLineItems: payload.manualLineItems,
        // The user submitted from inside Configure, so this IS their selection —
        // including a deliberate "everything unchecked" (§D.5).
        optionsFrozen: true,
        filters: rfqFilters,
      });
      // Sync the navbar/floating popup's auto-context with the actual selection
      // the user just submitted from inside Configure.
      dispatch(
        setLastConfigured({
          solutionProductId: configureContext.productId,
          solutionName: configureContext.product.name,
          solutionMachineType: machineType,
          applications: apps,
          applicationIds: configureContext.applicationIds,
          selectedOptions: payload.selectedOptions,
          selectedPartNos: payload.selectedPartNos,
          manualLineItems: payload.manualLineItems,
          region: configureContext.region,
          mspAutoFeedEnabled: configureContext.mspAutoFeedEnabled,
          requiredMachines: configureContext.requiredMachines,
          optionsFrozen: true,
          filters: rfqFilters,
        }),
      );
    } else {
      setRfqContext(null);
    }
    close();
    open("support");
  };

  // Configure → "Download quotation" / "Download Word": ad-hoc preview (never
  // persisted), gated by canGenerateQuote. `format` selects PDF vs DOCX.
  const downloadFromConfigure = (
    payload: ConfigurePayload,
    format: "pdf" | "docx",
  ) => {
    if (!configureContext) return;
    // PDF click is tracked server-side on the render endpoint (no UI event).
    if (format === "docx") {
      trackEvent("quote_docx_download_clicked", { region: configureContext.region });
    }
    const apps = appsForConfigure(configureContext);
    downloadAdHocQuote.mutate({
      format,
      language: "en",
      includeRoi: false,
      regionCode: configureContext.region,
      machineCostMode: "dedicated",
      solution: {
        productId: configureContext.productId,
        name: configureContext.product.name,
        machineType:
          configureContext.product.subcategory ||
          configureContext.product.category ||
          null,
      },
      applications: toDocApplications(apps),
      selectedOptions: toDocSelectedOptions(payload.selectedOptions),
      manualLineItems: payload.manualLineItems,
      assumptions: toDocAssumptions(assumptions),
      customer: {
        companyName: user?.companyName ?? null,
        contactName:
          [user?.firstName, user?.lastName].filter(Boolean).join(" ") || null,
        email: user?.email ?? null,
        phone: user?.phone ?? null,
        companyAddress: user?.companyAddress ?? null,
      },
    });
  };

  const handleDownloadQuotationFromConfigure = (payload: ConfigurePayload) =>
    downloadFromConfigure(payload, "pdf");
  const handleDownloadWordFromConfigure = (payload: ConfigurePayload) =>
    downloadFromConfigure(payload, "docx");

  return (
    // pb-28 keeps the last section's actions (Clear selection / Customize
    // assumptions) clear of the fixed bottom-right "Compare / Configure
    // Solutions" floating button, which previously overlapped them.
    <div className={`min-h-screen pb-36 sm:pb-28 ${actingForOther ? "pt-44 lg:pt-40" : "pt-34 lg:pt-30"}`}>
      <FilterPanel
        key={filterResetKey}
        onResults={handleResults}
        onStatus={handleStatus}
        onFilterChange={setCurrentFilters}
        onExistingApp={() => requireAuth("existing-apps")}
        onSetupNewApplication={handleSetupNewApplication}
        onResetAppDriven={() => {
          setSelectedApps([]);
          setAppDrivenBrands([]);
          setSelectedAppDetails([]);
          dispatch(clearSelector());
        }}
        onResetSelection={() => {
          setSelected([]);
          setSelectedMachineByApp({});
          setMspAutoFeedByProductId({});
          dispatch(clearSelector());
        }}
        selectedApps={selectedApps}
        appDrivenBrands={appDrivenBrands}
        appDrivenIds={appDrivenIds}
        compareSelectedCount={selected.length}
        seed={filterSeed}
      />
      <SolutionGrid
        products={products}
        selected={selected}
        onToggle={toggleSolution}
        isPending={evalStatus.isPending}
        isError={evalStatus.isError}
        errorMessage={evalStatus.errorMessage}
      />
      <ComparisonList
        onConfigure={handleConfigure}
        reoptimizeToken={reoptimizeToken}
        onRequiredMachinesByProduct={setLiveReqMachinesByProduct}
        onRemoveProduct={(productId) =>
          setSelected((prev) => prev.filter((id) => id !== productId))
        }
        products={products}
        selected={selected}
        selectedApps={selectedApps}
        selectedAppDetails={selectedAppDetails}
        region={region}
        isAuthenticated={isAuthenticated}
        canShowPrices={canShowCardPrices}
        canShowGlobalTotals={canShowPrices}
        canGenerateQuote={!!user?.role?.canGenerateQuote}
        canGenerateRoi={!!user?.role?.canGenerateRoi}
        selectedMachineByApp={selectedMachineByApp}
        onToggleMachineForApp={(appId, productId, checked) => {
          setSelectedMachineByApp((prev) => {
            const next = { ...prev };
            if (checked) next[appId] = productId;
            else if (next[appId] === productId) delete next[appId];
            return next;
          });
        }}
        mspAutoFeedByProductId={mspAutoFeedByProductId}
        onMspAutoFeedToggle={(productId) =>
          setMspAutoFeedByProductId((prev) => {
            const current = prev[productId] ?? true;
            return { ...prev, [productId]: !current };
          })
        }
        optimizeSessionId={optimizeSessionId}
        onApplyOptimizedAssignment={(assignment) =>
          setSelectedMachineByApp((prev) => ({ ...prev, ...assignment }))
        }
      />
      <ApplicationParameters
        selectedApps={selectedApps}
        selectedAppDetails={selectedAppDetails}
        isAuthenticated={isAuthenticated}
        onAddExisting={() => requireAuth("existing-apps")}
        onNewApp={() => requireAuth("new-app")}
        onEditApp={handleEditApp}
        onRemoveApp={(applicationId) => {
          const next = selectedApps.filter((a) => a.applicationId !== applicationId);
          setSelectedApps(next);
          void handleAppsSelected(next);
        }}
        onClearSelection={() => {
          setSelectedApps([]);
          setAppDrivenBrands([]);
          setSelectedAppDetails([]);
          // Mirror "Reset filters" machine clearing: also drop the selected
          // solutions, their per-application machine assignments and MSP
          // auto-feed toggles, not just the applications.
          setSelected([]);
          setSelectedMachineByApp({});
          setMspAutoFeedByProductId({});
          clearAllProductSelections();
          dispatch(clearSelector());
        }}
      />

      <FloatingCompareCTA
        count={selected.length}
        onCompare={handleFloatingConfigure}
      />

      {/* Sits one step above the bottom-right Compare CTA on phones so the
          two floating buttons never overlap on narrow screens. */}
      <div
        className={`fixed bottom-22 sm:bottom-6 left-4 sm:left-6 z-50 transition-all duration-300 ${
          showSupport ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
      >
        <PennButton
          variant="primary"
          size="md"
          onClick={() => {
            setRfqContext(null);
            requireAuth("support");
          }}
        >
          Request Support
        </PennButton>
      </div>

      <SupportModal
        open={modal === "support"}
        onClose={() => {
          close();
          setRfqContext(null);
        }}
        rfqContext={rfqContext}
      />
      <ExistingApplicationsModal
        open={modal === "existing-apps"}
        onClose={close}
        initialSelectedIds={appDrivenIds}
        onSelect={handleAppsSelected}
        onEdit={handleEditApp}
        onNew={() => {
          setEditingId(null);
          close();
          open("new-app");
        }}
      />
      <ApplicationEditorModal
        open={modal === "new-app"}
        onClose={close}
        mode="create"
        onSaved={handleSaved}
      />
      <ApplicationEditorModal
        open={modal === "edit-app"}
        onClose={() => {
          setEditingId(null);
          close();
        }}
        mode="edit"
        applicationId={editingId}
        onSaved={handleSaved}
      />
      {configureContext && (
        <ConfigureSolutionModal
          open={modal === "configure"}
          onClose={(changed) => {
            close();
            setConfigureContext(null);
            if (changed) setReoptimizeToken((t) => t + 1);
          }}
          productId={configureContext.productId}
          product={configureContext.product}
          region={configureContext.region}
          applicationIds={configureContext.applicationIds}
          mspAutoFeedEnabled={
            mspAutoFeedByProductId[configureContext.productId] ?? true
          }
          requiredMachines={
            liveReqMachinesByProduct[configureContext.productId] ??
            configureContext.requiredMachines
          }
          canShowPrices={canShowPrices}
          isAuthenticated={isAuthenticated}
          canGenerateQuote={!!user?.role?.canGenerateQuote}
          canWorkForOthers={!!user?.role?.canWorkForOthers}
          appSummary={configureAppSummary}
          taskContext={configureContext.taskContext}
          initialSelectedOptions={configureContext.initialSelectedOptions}
          initialSelectedPartNos={configureContext.initialSelectedPartNos}
          initialCustomToolingText={configureContext.initialCustomToolingText}
          onRequestQuotation={handleRequestQuotation}
          onDownloadQuotation={handleDownloadQuotationFromConfigure}
          onDownloadWord={handleDownloadWordFromConfigure}
        />
      )}
    </div>
  );
};
